import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { MessageService, SelectItem } from "primeng/api";
import { forkJoin, Observable, tap } from "rxjs";
import { ChartOfAccounts } from "../chart-of-accounts.model";
import { COAAccountType } from "../coa-account-type.model";
import { ChartOfAccountsService } from "../chart-of-accounts.service";
import { SessionStorageService } from "../../services/session-storage.service";
import { ButtonModule } from 'primeng/button';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { SelectModule } from 'primeng/select';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { InputTextModule } from 'primeng/inputtext';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';

@Component({
    selector: 'app-add-edit-accounts',
    templateUrl: './add-edit-accounts.component.html',
    styleUrls: ['./add-edit-accounts.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    SelectModule,
    ProgressBarModule,
    DividerModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
],
    providers: [
        ChartOfAccountsService,
        MessageService,
        SessionStorageService
    ]
})
export class AddEditAccountsComponent implements OnInit {

    chartOfAccounts!: ChartOfAccounts;
    chartOfAccountsForm!: FormGroup;
    editMode = false;
    accountId!: number;
    isLoading: boolean = false;

    accountTypes: SelectItem<COAAccountType>[] = [];
    parentChartOfAccount: ChartOfAccounts | undefined;
    readonly protectedPostingAccountNames = new Set([
        'Sales',
        'Trade Accounts Receivable',
        'Sales Tax Payable',
        'Cash and Cash Equivalents',
        'Trade Accounts Payable',
        'Driver Advances',
        'Employee Advances',
        'Expenses',
        'Input VAT Account',
        'Driver Trip Expenses',
        'Workforce Cash Variance',
        'Workforce Cash Return'
    ]);

    constructor(
        private fb: FormBuilder, private route: ActivatedRoute,
        private location: Location, private router: Router,
        private messageService: MessageService,
        private chartOfAccountsService: ChartOfAccountsService,
        private sessionStorageService: SessionStorageService) {
    }

    ngOnInit(): void {
        this.initForm();

        this.route.params.subscribe(params => {
            this.accountId = params['id'];
            this.editMode = this.route.snapshot.url[0].path.includes('edit');
            this.loadData();
        });
    }

    loadData() {
        this.isLoading = true;

        const loadObservables = {
            accountTypes: this.loadAccountTypes()
        };

        forkJoin(loadObservables).subscribe({
                next: () => {
                    this.loadChartOfAccounts().subscribe({
                        error: (error) => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'An error occurred while loading account data.' + error.message
                            });
                            this.isLoading = false;
                        },
                        complete: () => {
                            this.isLoading = false;
                        }
                    });
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'An error occurred while loading data.' + error.message
                    });
                    this.isLoading = false;
                }
            });
    }

    loadAccountTypes() {
        return this.chartOfAccountsService.getAllAccountTypes().pipe(
            tap(value => {
                this.accountTypes = value.map(accountType => ({
                    value: accountType,
                    label: accountType.accountTypeName
                }));
            }),
        );
    }

    loadChartOfAccounts() {
        return this.chartOfAccountsService.getChartOfAccountsById(this.accountId, true).pipe(
            tap(chartOfAccounts => {
                this.chartOfAccounts = chartOfAccounts;
                if (this.editMode) {
                    this.parentChartOfAccount = chartOfAccounts.parentAccount ?? undefined;
                    this.chartOfAccountsForm.patchValue(chartOfAccounts);
                    this.applyProtectedAccountControls();
                } else {
                    const newAccountCode = this.nextChildAccountCode(chartOfAccounts);
                    const newAccountPrefix = chartOfAccounts.accountCode.replace(/-00/g, '');
                    this.accountTypes = this.accountTypes.filter(type => type.value.codePrefix.startsWith(newAccountPrefix));
                    if (this.accountTypes.length === 0)
                        this.accountTypes = [{ value: chartOfAccounts.accountType, label: chartOfAccounts.accountType.accountTypeName }];
                    this.chartOfAccountsForm.reset();
                    this.parentChartOfAccount = chartOfAccounts;
                    this.chartOfAccountsForm.patchValue({ accountCode: newAccountCode });
                    this.chartOfAccountsForm.patchValue({ parentAccount: chartOfAccounts });
                    this.chartOfAccountsForm.patchValue({ accountType: this.parentChartOfAccount?.accountType });
                    this.chartOfAccountsForm.patchValue({ internalType: this.parentChartOfAccount?.internalType ?? '' });
                    this.chartOfAccountsForm.patchValue({ currencyCode: this.parentChartOfAccount?.currencyCode ?? this.defaultCurrencyCode() });
                }
            })
        );
    }

    initForm() {
        this.chartOfAccountsForm = this.fb.group({
            accountId: [null],
            accountCode: ['', Validators.required],
            accountName: ['', Validators.required],
            accountType: [null, Validators.required],
            internalType: [''],
            currencyCode: [''],
        });
    }

    saveChartOfAccounts() {
        if (this.chartOfAccountsForm.invalid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct all form errors.'
            });
            return;
        }
        this.isLoading = true;
        const modifiedChartOfAccounts = {
            ...(this.editMode ? this.chartOfAccounts : {}),
            ...this.chartOfAccountsForm.getRawValue()
        } as ChartOfAccounts;
        modifiedChartOfAccounts.owningCompanyId = this.sessionStorageService.getCompany()!.id;
        modifiedChartOfAccounts.parentAccount = this.parentChartOfAccount ?? null;
        modifiedChartOfAccounts.isActive = this.editMode ? (this.chartOfAccounts.isActive ?? true) : true;
        modifiedChartOfAccounts.allowEditing = this.editMode ? this.chartOfAccounts.allowEditing : true;
        modifiedChartOfAccounts.deprecated = this.editMode ? this.chartOfAccounts.deprecated : false;
        modifiedChartOfAccounts.allowReconciliation = this.editMode ? this.chartOfAccounts.allowReconciliation : false;
        modifiedChartOfAccounts.internalType = this.editMode
            ? this.chartOfAccounts.internalType : this.parentChartOfAccount?.internalType ?? '';
        modifiedChartOfAccounts.currencyCode = modifiedChartOfAccounts.currencyCode || this.chartOfAccounts?.currencyCode || this.parentChartOfAccount?.currencyCode || this.defaultCurrencyCode();
        // The code shown in the form is a preview. Allocate it against current data when saving.
        if (!this.editMode) {
            modifiedChartOfAccounts.accountCode = '';
        }

        if (this.editMode && this.isProtectedPostingAccount(this.chartOfAccounts)) {
            modifiedChartOfAccounts.accountName = this.chartOfAccounts.accountName;
            modifiedChartOfAccounts.accountType = this.chartOfAccounts.accountType;
            modifiedChartOfAccounts.internalType = this.chartOfAccounts.internalType;
        }

        const saveRequest: Observable<unknown> = this.editMode
            ? this.chartOfAccountsService.updateChartOfAccounts(modifiedChartOfAccounts.owningCompanyId!, modifiedChartOfAccounts.accountId, modifiedChartOfAccounts)
            : this.chartOfAccountsService.createChartOfAccounts(modifiedChartOfAccounts);

        saveRequest.subscribe({
            next: () => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Chart of Account saved successfully.'
                });
                this.router.navigate(['/chart-of-accounts']);
            },
            error: (error: any) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error saving chart of account: ' + error.error.message
                });
            }
        });
    }

    cancel() {
        this.location.back();
    }

    get pageTitle(): string {
        return this.editMode ? 'Edit Chart of Account' : 'Add Sub-Account';
    }

    get pageSubtitle(): string {
        if (this.editMode) {
            return this.isProtectedCurrentAccount
                ? 'Protected posting account. Name and account type are locked to preserve existing posting automation.'
                : 'Update account master data without changing posted accounting history.';
        }
        return `Create a child under ${this.parentChartOfAccount?.accountName ?? 'the selected parent account'}.`;
    }

    get isProtectedCurrentAccount(): boolean {
        return this.editMode && this.isProtectedPostingAccount(this.chartOfAccounts);
    }

    get parentAccountCode(): string {
        return this.parentChartOfAccount?.accountCode || this.chartOfAccounts?.parentAccount?.accountCode || '-';
    }

    get parentAccountName(): string {
        return this.parentChartOfAccount?.accountName || this.chartOfAccounts?.parentAccount?.accountName || 'Root account';
    }

    get parentAccountTypeName(): string {
        return this.parentChartOfAccount?.accountType?.accountTypeName || this.chartOfAccounts?.parentAccount?.accountType?.accountTypeName || '-';
    }

    isProtectedPostingAccount(account?: ChartOfAccounts): boolean {
        return !!account?.accountName && this.protectedPostingAccountNames.has(account.accountName);
    }

    private applyProtectedAccountControls(): void {
        if (this.isProtectedCurrentAccount) {
            this.chartOfAccountsForm.get('accountName')?.disable({ emitEvent: false });
            this.chartOfAccountsForm.get('accountType')?.disable({ emitEvent: false });
        } else {
            this.chartOfAccountsForm.get('accountName')?.enable({ emitEvent: false });
            this.chartOfAccountsForm.get('accountType')?.enable({ emitEvent: false });
        }
    }

    private defaultCurrencyCode(): string {
        return this.sessionStorageService.getCountryList()
            ?.find(value => value.id == this.sessionStorageService.getCompany()?.countryId)?.currencyCode ?? '';
    }

    private nextChildAccountCode(parent: ChartOfAccounts): string {
        const segments = parent.accountCode.split('-');
        const childLevel = segments.indexOf('00');
        if (segments.length !== 6 || childLevel < 0) {
            return '';
        }
        const occupied = new Set((parent.childrenAccounts ?? []).map(child => child.accountCode));
        let nextNumber = 1;
        for (let number = 1; number <= 99; number++) {
            const candidate = [...segments];
            candidate[childLevel] = number.toString().padStart(2, '0');
            const code = candidate.join('-');
            if (occupied.has(code)) {
                nextNumber = number + 1;
            }
        }
        if (nextNumber <= 99) {
            const candidate = [...segments];
            candidate[childLevel] = nextNumber.toString().padStart(2, '0');
            return candidate.join('-');
        }
        for (let number = 1; number <= 99; number++) {
            const candidate = [...segments];
            candidate[childLevel] = number.toString().padStart(2, '0');
            if (!occupied.has(candidate.join('-'))) {
                return candidate.join('-');
            }
        }
        return '';
    }
}
